import { useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  notificationPreferenceService,
  type NotificationPreferences,
} from "../services/notification-preference.service";
import { useAuthStore } from "../stores/authStore";

const PREFS_KEY = ["notification-preferences"];

const DEFAULTS: NotificationPreferences = {
  lowStockEnabled: true,
  lowStockThreshold: 10,
  stockExpiryEnabled: true,
  stockExpiryDays: 30,
};

export function useNotificationPreferences() {
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const {
    data: preferences = DEFAULTS,
    isLoading,
    isError,
  } = useQuery<NotificationPreferences>({
    queryKey: PREFS_KEY,
    queryFn: () => notificationPreferenceService.get(),
    enabled: isAuthenticated,
    staleTime: 5 * 60_000, // Cache for 5 minutes
  });

  const mutation = useMutation({
    mutationFn: (updates: Partial<NotificationPreferences>) =>
      notificationPreferenceService.update(updates),
    onMutate: async (updates) => {
      await queryClient.cancelQueries({ queryKey: PREFS_KEY });
      const previous =
        queryClient.getQueryData<NotificationPreferences>(PREFS_KEY);
      queryClient.setQueryData<NotificationPreferences>(PREFS_KEY, (old) => ({
        ...(old || DEFAULTS),
        ...updates,
      }));
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(PREFS_KEY, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: PREFS_KEY });
    },
  });

  const updatePreferences = useCallback(
    (updates: Partial<NotificationPreferences>) => mutation.mutate(updates),
    [mutation],
  );

  return {
    preferences,
    isLoading,
    isError,
    isSaving: mutation.isPending,
    isSaved: mutation.isSuccess,
    updatePreferences,
  };
}
